import React from "react";
import { Icon } from "../core/Icon.jsx";

/* Autocomplete — native typeahead combobox (no MUI, no external lib).
   Combobox a11y: role="combobox" + aria-expanded + aria-activedescendant, a
   listbox of options, keyboard nav (Up/Down/Enter/Esc). Replaces
   @material-ui/lab Autocomplete (REG-378 part 3, FR-002).

   Options are strings or { value, label }. For very large lists, list
   windowing can be layered on later (see spec AM2); the current build renders
   the filtered set directly, which covers the existing call sites.

   Server-side search: pass onInputChange to be notified of every query
   keystroke so the parent can refetch options from the backend. Pair with
   filterOptions={(x) => x} to bypass the internal client-side filter when the
   backend has already narrowed results (needed when the server can match on
   fields the client-side labelOf doesn't see, or when the backend truncates
   results). */

const labelOf = (opt, getOptionLabel) => {
  if (getOptionLabel) return getOptionLabel(opt);
  if (opt && typeof opt === "object") return opt.label ?? String(opt.value ?? "");
  return String(opt ?? "");
};

export function Autocomplete({
  options = [],
  value = null,
  onChange,
  onInputChange,
  filterOptions,
  getOptionLabel,
  renderOption,
  placeholder,
  label,
  loading = false,
  disabled = false,
  id,
  style,
  ...rest
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [active, setActive] = React.useState(0);
  const rootRef = React.useRef(null);
  const listId = `${id || "ac"}-listbox`;

  const display = value != null ? labelOf(value, getOptionLabel) : query;
  const filtered = React.useMemo(() => {
    if (typeof filterOptions === "function") return filterOptions(options, query);
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => labelOf(o, getOptionLabel).toLowerCase().includes(q));
  }, [options, query, getOptionLabel, filterOptions]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const choose = (opt) => {
    setOpen(false);
    setQuery("");
    if (onChange) onChange(opt);
    if (onInputChange) onInputChange("");
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      if (open && filtered[active]) {
        e.preventDefault();
        choose(filtered[active]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={rootRef} style={{ position: "relative", ...style }} {...rest}>
      {label ? (
        <label
          htmlFor={id}
          className="t-p-small-medium"
          style={{ display: "block", marginBottom: 6, color: "var(--text-strong)" }}
        >
          {label}
        </label>
      ) : null}
      <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
        <input
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && filtered[active] ? `${listId}-${active}` : undefined}
          disabled={disabled}
          value={display}
          placeholder={placeholder}
          onChange={(e) => {
            const next = e.target.value;
            setQuery(next);
            setActive(0);
            setOpen(true);
            if (value != null && onChange) onChange(null);
            if (onInputChange) onInputChange(next);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          style={{
            width: "100%",
            height: 40,
            padding: "0 36px 0 12px",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-md)",
            background: "var(--surface-card)",
            color: "var(--text-strong)",
            fontFamily: "var(--font-sans)",
            fontSize: 14,
          }}
        />
        <span style={{ position: "absolute", right: 10, color: "var(--text-muted)", pointerEvents: "none" }}>
          <Icon name="chevronDown" size={16} />
        </span>
      </div>
      {open ? (
        <ul
          role="listbox"
          id={listId}
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            marginTop: 4,
            zIndex: 1000,
            maxHeight: 260,
            overflowY: "auto",
            listStyle: "none",
            margin: 0,
            padding: 4,
            background: "var(--surface-card)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-md)",
            boxShadow: "var(--shadow-4)",
          }}
        >
          {loading ? (
            <li style={{ padding: "10px 10px", color: "var(--text-muted)", fontFamily: "var(--font-sans)", fontSize: 13 }}>
              Loading…
            </li>
          ) : filtered.length === 0 ? (
            <li style={{ padding: "10px 10px", color: "var(--text-muted)", fontFamily: "var(--font-sans)", fontSize: 13 }}>
              No matches
            </li>
          ) : (
            filtered.map((opt, i) => (
              <li
                key={(opt && opt.value) ?? labelOf(opt, getOptionLabel) ?? i}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === active}
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(opt);
                }}
                onMouseEnter={() => setActive(i)}
                style={{
                  padding: "8px 10px",
                  borderRadius: "var(--radius-sm)",
                  cursor: "pointer",
                  background: i === active ? "var(--grey-bg)" : "transparent",
                  color: "var(--text-body)",
                  fontFamily: "var(--font-sans)",
                  fontSize: 14,
                }}
              >
                {renderOption ? renderOption(opt) : labelOf(opt, getOptionLabel)}
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}

export default Autocomplete;
